import {md2html} from '@github-ui/md2html'
import {DOCUMENT_FIELDS_PATH, type SetFieldsOperation} from '@github-ui/optimistic-connection'
import {ENTITY_FIELDS_OWNER} from '@github-ui/optimistic-connection/pendingOperations'
import {notifySettlement} from '@github-ui/optimistic-connection/settlementRegistry'
import {useEntityMutation} from '@github-ui/optimistic-connection/useEntityMutation'
import {useMutation} from '@github-ui/react-query'
import {useCallback} from 'react'

import {updateIssueBody, type UpdateIssueBodyInput, type UpdateIssueBodyIssue} from './api-update-issue-body'
import {reportAsyncError} from './report-async-error'

export type UpdateIssueBodyCompletion = {
  readonly updateIssue: {
    readonly issue: {readonly bodyVersion: string} | null
  } | null
}

type CommitUpdateIssueBodyOptions = {
  input: {issueId: string; body: string; bodyVersion: string}
  withOptimisticResponse?: boolean
  onCompleted?: (response: UpdateIssueBodyCompletion) => void
  onError?: (error: Error) => void
}

function toIssueBodyOperation(issue: UpdateIssueBodyIssue): SetFieldsOperation {
  return {
    type: 'setFields',
    id: issue.id,
    fields: {
      body: issue.body,
      bodyHTML: issue.bodyHTML,
      bodyVersion: issue.bodyVersion,
      lastEditedAt: issue.lastEditedAt,
      viewerCanReadUserContentEdits: issue.viewerCanReadUserContentEdits,
      lastUserContentEdit: issue.lastUserContentEdit,
    },
  }
}

function inputFromOperation(operation: SetFieldsOperation): UpdateIssueBodyInput {
  const {body, bodyVersion} = operation.fields
  if (typeof operation.id !== 'string' || typeof body !== 'string' || typeof bodyVersion !== 'string') {
    throw new Error('Invalid issue body operation')
  }
  return {id: operation.id, body, bodyVersion}
}

function errorFromUnknown(error: unknown): Error {
  return error instanceof Error ? error : new Error('Failed to update issue body')
}

export function useUpdateIssueBodyMutation({
  owner,
  repo,
  issueNumber,
}: {
  owner: string
  repo: string
  issueNumber: number
}) {
  const {mutateAsync: mutateOptimistically} = useEntityMutation<UpdateIssueBodyIssue>({
    mutationFn: operation => updateIssueBody({owner, repo, issueNumber, input: inputFromOperation(operation)}),
    getConfirmedOperation: issue => toIssueBodyOperation(issue),
  })

  const {mutateAsync: mutateWithoutOptimism} = useMutation<UpdateIssueBodyIssue, Error, UpdateIssueBodyInput>({
    mutationFn: input => updateIssueBody({owner, repo, issueNumber, input}),
    onSuccess: async issue => {
      await notifySettlement(toIssueBodyOperation(issue), ENTITY_FIELDS_OWNER, DOCUMENT_FIELDS_PATH)
    },
  })

  return useCallback(
    ({input, withOptimisticResponse = false, onCompleted, onError}: CommitUpdateIssueBodyOptions) => {
      const nativeInput = {id: input.issueId, body: input.body, bodyVersion: input.bodyVersion}
      const commit = async () => {
        let issue: UpdateIssueBodyIssue
        try {
          issue = withOptimisticResponse
            ? await mutateOptimistically({
                operation: toIssueBodyOperation({
                  ...nativeInput,
                  bodyHTML: md2html(input.body),
                  lastEditedAt: new Date().toISOString(),
                  viewerCanReadUserContentEdits: true,
                  lastUserContentEdit: null,
                }),
              })
            : await mutateWithoutOptimism(nativeInput)
        } catch (error) {
          onError?.(errorFromUnknown(error))
          return
        }

        // The write and store update have already committed. Run the consumer success callback
        // outside the network/cache try boundary so a throwing onCompleted is surfaced to the
        // global error handler rather than routed to onError and misreported as a failed mutation.
        try {
          onCompleted?.({updateIssue: {issue: {bodyVersion: issue.bodyVersion}}})
        } catch (error) {
          reportAsyncError(error)
        }
      }

      void commit()
    },
    [mutateOptimistically, mutateWithoutOptimism],
  )
}
